(() => {
  if (customElements.get('hh-product-specifications')) return;
  const clean = value => {
    if (Array.isArray(value)) return value.map(clean).filter(Boolean).join(', ');
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  };
  const parseSpecs = value => {
    if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return []; } }
    return Array.isArray(value) ? value : [];
  };
  const keyFor = label => {
    const key = clean(label).toLowerCase().replace(/[\s:()._\-/]/g, '');
    return ({brand:'brand',mærke:'brand',varemærke:'brand',fabrikant:'brand',producent:'brand',
      ean:'barcode',ean13:'barcode',barcode:'barcode',stregkode:'barcode',gtin:'barcode',
      sku:'sku',varenr:'sku',varenummer:'sku',material:'material',materialer:'material',materiale:'material',
      colour:'color',color:'color',farve:'color',weight:'weight',vægt:'weight',vægtkg:'weight',vægtg:'weight',
      modelnavn:'model',model:'model',gender:'gender',køn:'gender',fit:'fit',pasform:'fit'})[key] || key;
  };
  const labelFor = label => ({brand:'Varemærke',barcode:'EAN',material:'Materiale',color:'Farve',model:'Model',gender:'Køn',fit:'Pasform'})[keyFor(label)] || clean(label);
  const safeUrl = value => {
    try { const url = new URL(clean(value)); return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : ''; }
    catch { return ''; }
  };
  const catalogPages = value => {
    const pages = new Set();
    for (const entry of (Array.isArray(value) ? value : [value])) {
      for (const token of clean(entry).split(/[,;\n/]+/)) {
        const match = token.trim().match(/^(?:side[r]?\.?\s*)?(\d{1,4})(?:\s*[-–]\s*(\d{1,4}))?$/i);
        if (!match) continue;
        const first = Number(match[1]), last = Number(match[2] || match[1]);
        if (first < 1 || last < first || last - first > 20) continue;
        for (let page = first; page <= last && pages.size < 25; page++) pages.add(page);
      }
    }
    return [...pages];
  };
  function detailRows(variant, details) {
    if (!variant) return [];
    const rows = new Map();
    const add = (label, value, url = '') => {
      label = labelFor(label); value = clean(value);
      const key = keyFor(label);
      if (!label || !value || key === 'sku' || rows.has(key)) return;
      rows.set(key, {label, value, url: safeUrl(url)});
    };
    const specs = parseSpecs(variant.specs);
    for (const spec of specs) {
      if (!spec || !clean(spec.label)) continue;
      let value = clean(spec.value);
      const unit = clean(spec.unit);
      if (unit && value && !value.endsWith(unit)) value += ' ' + unit;
      const url = safeUrl(spec.url || spec.href || value);
      const isDocument = /manual|vejledning|datablad|sikkerhedsblad|sikkerhedsdatablad|dokument|certifikat|producent.*link|link.*producent/i.test(spec.label);
      if (url) add(spec.label, isDocument ? 'Åbn dokument' : 'Åbn link', url);
      else add(spec.label, value);
    }
    // Existing structured facts take precedence over options and product-wide fallbacks.
    for (const option of variant.extra?.options || []) {
      if (/^(default title|default)$/i.test(clean(option.value))) continue;
      add(/^(title|titel)$/i.test(clean(option.label)) ? 'Variant' : option.label, option.value);
    }
    const legacy = variant.extra?.legacySpecs || details.legacySpecs;
    const legacyValues = Array.isArray(legacy) ? legacy : typeof legacy === 'string' ? legacy.split(/\r?\n/) : [];
    if (!specs.length && legacyValues.length) {
      const other = [];
      for (const fact of legacyValues) {
        const text = clean(fact), split = text.indexOf(':');
        if (split > 0 && !safeUrl(text)) add(text.slice(0, split), text.slice(split + 1));
        else if (text) other.push(text);
      }
      add('Egenskaber', other);
    }
    const supplierNames = ['havhøkeren','havhokeren','palby','palby marine','palby marine (havhøkeren)','engholm','columbus marine','marineworld','scanmarine','delite','godtbergsen','hf marine','focus nordic','frydendahl'];
    if (!supplierNames.includes(clean(details.vendor).toLowerCase())) add('Varemærke', details.vendor);
    // The selected variant's barcode is authoritative, including leading zeroes.
    const barcode = clean(variant.extra?.barcode);
    if (barcode) { rows.delete('barcode'); add('EAN', barcode); }
    const weight = Number(variant.extra?.weight), unit = clean(variant.extra?.weightUnit);
    if (Number.isFinite(weight) && weight > 0 && ['g','kg','oz','lb'].includes(unit)) {
      add('Vægt', new Intl.NumberFormat('da-DK', {maximumFractionDigits: 3}).format(weight) + ' ' + unit);
    }
    for (const field of details.fields || []) add(field.label, field.value);
    const ordered = ['brand','barcode'].flatMap(key => rows.has(key) ? [rows.get(key)] : []);
    ordered.push(...[...rows.entries()].filter(([key]) => !['brand','barcode'].includes(key)).map(([,row]) => row));
    if (/^PBM-/i.test(clean(variant.sku))) {
      const pages = catalogPages(details.catalogPages);
      const selectedPages = pages.length ? pages : catalogPages(details.legacyCatalogPages);
      if (selectedPages.length) ordered.push({label:'Palby-katalog 2025', links:selectedPages.map(page => ({
        value:`Se side ${page}`, url:`https://palby.paperturn-view.com/palby-marine-katalog-2025?pid=ODg8866535&p=${page}&v=5`
      }))});
    }
    return ordered;
  }
  class ProductSpecifications extends HTMLElement {
    static detailRows(variant, details) { return detailRows(variant, details); }
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      const data = JSON.parse(this.querySelector('[data-specification-data]').textContent);
      this.details = data.details;
      this.variants = new Map(data.variants.map(([id,sku,specs,extra]) => [String(id), {sku,specs:specs ?? data.fallback,extra}]));
      this.unsubscribe?.();
      const productRoot = document.querySelector(`.product-template[data-product-id="${this.dataset.productId}"]`);
      const selector = productRoot?.querySelector('select[data-variant-select]');
      if (window.MinimogEvents && this.dataset.productId) {
        this.unsubscribe = window.MinimogEvents.subscribe(`${this.dataset.productId}__VARIANT_CHANGE`, (variant, source) => {
          const container = source instanceof Element ? source : source?.container;
          if (container && !productRoot?.contains(container)) return;
          this.update(variant?.id);
        });
      }
      selector?.addEventListener('change', () => this.update(selector.value), {signal});
      this.update(selector?.value || productRoot?.querySelector('variant-picker')?.currentVariant?.id || this.dataset.initialVariant);
    }
    disconnectedCallback() { this.controller?.abort(); this.unsubscribe?.(); }
    update(id) {
      const variant = this.variants.get(String(id));
      this.dataset.currentVariant = String(id);
      this.querySelector('[data-specification-sku]').textContent = variant?.sku || '';
      this.querySelector('[data-specification-item]').hidden = !variant?.sku;
      const list = document.createElement('ul');
      if (this.details) {
        for (const row of detailRows(variant, this.details)) {
          const item = document.createElement('li'), label = document.createElement('strong');
          label.textContent = row.label;
          item.append(label);
          if (row.links || row.url) {
            const links = document.createElement('div'); links.className = 'hh-product-details__links';
            for (const link of row.links || [row]) {
              const anchor = document.createElement('a');
              anchor.href = link.url; anchor.textContent = link.value;
              anchor.target = '_blank'; anchor.rel = 'noopener noreferrer';
              links.append(anchor);
            }
            item.append(links);
          } else {
            const value = document.createElement('span'); value.textContent = row.value; item.append(value);
          }
          list.append(item);
        }
      } else {
        for (const spec of parseSpecs(variant?.specs)) {
          if (!spec || typeof spec.label !== 'string' || !spec.label.trim() || spec.value == null) continue;
          if (typeof spec.value !== 'string' && typeof spec.value !== 'number') continue;
          let value = String(spec.value).trim();
          if (!value) continue;
          const unit = typeof spec.unit === 'string' ? spec.unit.trim() : '';
          if (unit && !value.endsWith(unit)) value += ' ' + unit;
          const item = document.createElement('li'), label = document.createElement('strong');
          label.textContent = spec.label.trim() + ':';
          item.append(label, document.createTextNode(' ' + value)); list.append(item);
        }
      }
      const content = this.querySelector('[data-specification-content]');
      if (list.children.length) content.replaceChildren(list);
      else {
        const empty = document.createElement('p'); empty.textContent = this.dataset.emptyText; content.replaceChildren(empty);
      }
    }
  }
  customElements.define('hh-product-specifications', ProductSpecifications);
})();
